/* eslint-disable complexity */
import { memo, Suspense, useEffect } from 'react';

import {
  DEFAULT_ERROR_FALLBACK as ErrorFallback,
  DEFAULT_DELAYED_FALLBACK as DelayLoader,
} from './config/constants';

import OnIdle from '../IdleUntilUrgent';
import { DELAYS } from '../../utils/tti';

import { isRemoteLoaded } from './helpers/remote';
//@ts-ignore
import { noop } from '@mindtickle/utils';
import useDynamicScript from './hooks/useDynamicScript';
import useRemoteInitialisation from './hooks/useRemoteInitialisation';

import type { TLazyRemote } from './types';
import usePerformanceLoggerForRemote from './hooks/usePerformanceLoggerForRemote';
import { getShellTrackValues } from './helpers/remote';

function LoadRemote({
  remote,
  lazy = true,
  loader = <DelayLoader />,
  children: onRender,
  maxDelay = DELAYS.L,
  onIdleEnabled = true,
  onDownloadCompleted = noop,
  shouldFailSilently = false,
  isReactComponent = true,
  shouldRenderAsChildren = false,
  ...props
}: TLazyRemote) {
  const { onLoad, onModuleEvaluation } = usePerformanceLoggerForRemote(remote);
  const { shellTrack, track } = getShellTrackValues();
  let remoteUrl = remote?.url || '';

  if (shellTrack && remoteUrl.includes(`/${track}/`)) {
    remoteUrl = remoteUrl.replace(`/${track}/`, `/${shellTrack}/`);
  }

  const { ready: isManifestReady, failed } = useDynamicScript({
    url: remoteUrl,
    alreadyInitialised: isRemoteLoaded(remote.remoteName),
    onLoad,
  });

  const { isReadyForRendering, downloadCompleted, moduleDetails } = useRemoteInitialisation(
    remote,
    isManifestReady,
    lazy,
    maxDelay,
    shouldFailSilently,
    onModuleEvaluation
  );

  useEffect(() => {
    if (downloadCompleted) {
      onDownloadCompleted();
    }
  }, [downloadCompleted]);

  if (!remote) {
    return <h2>No remote specified</h2>;
  }

  if (failed) {
    return <>{!shouldFailSilently && <ErrorFallback />}</>;
  }

  if (!isManifestReady || !isReadyForRendering) {
    return loader;
  }

  const { Component, module } = moduleDetails!;

  const innerComponent = (
    <>
      {isReactComponent && (
        <Component {...props}>{shouldRenderAsChildren ? onRender : null}</Component>
      )}
      {!shouldRenderAsChildren && onRender && onRender(module)}
    </>
  );

  const onIdleWrapped = (
    <OnIdle placeholder={loader} syncUpdate={true}>
      {innerComponent}
    </OnIdle>
  );

  return <Suspense fallback={loader}>{onIdleEnabled ? onIdleWrapped : innerComponent}</Suspense>;
}

export default memo(LoadRemote);
